// Compare-Page PDF export — vector layout via jsPDF + autoTable.
// Premium A4 design: red header band, hero row (image + title + price), attribute table, footer.
//
// Public API:
//   await exportComparePdf({ cars, attributes, locale = "de", filenameSlug })
// `cars` = array of full listing docs (with images[]); nulls are skipped.
// `attributes` = same ATTRIBUTES array used by Compare.jsx so we stay in sync.

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const I18N = {
  de: { title: "Fahrzeugvergleich", subtitle: "Erstellt mit CarHive", date: "Erstellt am", attribute: "Attribut", page: "Seite", footer: "Provisionsfrei inserieren auf carhive.ch", price: "Preis", boost: "Empfohlen", dealer: "Händler" },
  fr: { title: "Comparatif de véhicules", subtitle: "Créé avec CarHive", date: "Créé le", attribute: "Attribut", page: "Page", footer: "Annonce sans commission sur carhive.ch", price: "Prix", boost: "Recommandé", dealer: "Concessionnaire" },
  it: { title: "Confronto veicoli", subtitle: "Creato con CarHive", date: "Creato il", attribute: "Attributo", page: "Pagina", footer: "Annuncia senza commissioni su carhive.ch", price: "Prezzo", boost: "Consigliato", dealer: "Concessionario" },
  en: { title: "Vehicle comparison", subtitle: "Created with CarHive", date: "Created on", attribute: "Attribute", page: "Page", footer: "Commission-free listings on carhive.ch", price: "Price", boost: "Featured", dealer: "Dealer" },
};

const COLOR_RED = [220, 38, 38];
const COLOR_BG_DARK = [10, 10, 10];
const COLOR_TXT_DARK = [24, 24, 27];
const COLOR_TXT_MUTED = [113, 113, 122];
const COLOR_LINE = [228, 228, 231];
const COLOR_HIGHLIGHT = [220, 252, 231]; // soft emerald wash for "best"-cell
const COLOR_DIFF = [254, 243, 199];      // soft amber wash for rows with differences

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 14;

/** Convert a remote image URL (or data URL) to a JPEG data URL via canvas.
 *  Resized to maxSide px on the longest edge. Returns null on any failure.
 */
async function imageToDataUrl(src, maxSide = 360) {
  if (!src) return null;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = src;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const c = document.createElement("canvas");
    c.width = cw;
    c.height = ch;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    return c.toDataURL("image/jpeg", 0.78);
  } catch {
    return null;
  }
}

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[äÄ]/g, "ae").replace(/[öÖ]/g, "oe").replace(/[üÜ]/g, "ue").replace(/[ß]/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}

export function buildCompareFilename(cars) {
  const valid = (cars || []).filter(Boolean);
  if (valid.length === 0) return "carhive-compare.pdf";
  const parts = valid.map((c) => slugify(`${c.brand || ""}-${c.model || ""}`)).filter(Boolean);
  if (parts.length === 0) return "carhive-compare.pdf";
  return `carhive-compare-${parts.join("-vs-")}.pdf`;
}

/** Attribute differs across given cars (used to flag rows). */
function attrDiffers(key, cars) {
  const norm = (x) => (x == null || x === "" ? null : x);
  const set = new Set(cars.map((c) => norm(c?.[key])));
  return set.size > 1;
}

/** Returns index of the single best car for a numeric attribute, or -1 on tie/none. */
function attrBestIndex(meta, cars) {
  if (!meta.lowerIsBetter && !meta.higherIsBetter) return -1;
  const vals = cars.map((c) => (c == null ? null : c[meta.key]));
  const numeric = vals.map((v) => (typeof v === "number" ? v : null));
  if (numeric.every((v) => v == null)) return -1;
  let bestIdx = -1;
  let bestVal = null;
  numeric.forEach((v, i) => {
    if (v == null) return;
    if (bestVal == null) { bestVal = v; bestIdx = i; return; }
    if (meta.lowerIsBetter && v < bestVal) { bestVal = v; bestIdx = i; }
    if (meta.higherIsBetter && v > bestVal) { bestVal = v; bestIdx = i; }
  });
  const tieCount = numeric.filter((v) => v === bestVal).length;
  return tieCount > 1 ? -1 : bestIdx;
}

function fmtCHF(v) {
  if (v == null || isNaN(v)) return "";
  return `CHF ${new Intl.NumberFormat("de-CH").format(Math.round(v))}`;
}

/** Resolve absolute image URL. Accepts already-absolute URLs or backend-relative
 *  paths like "carhive/uploads/..." which the backend serves at /api/files/{path}. */
function absImageUrl(raw, apiBase) {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (!apiBase) return null;
  return `${apiBase.replace(/\/$/, "")}/api/files/${raw.replace(/^\//, "")}`;
}

export async function exportComparePdf({ cars, attributes, locale = "de", apiBase = "" }) {
  const t = I18N[locale] || I18N.de;
  const validCars = (cars || []).filter(Boolean).slice(0, 3);
  if (validCars.length === 0) throw new Error("No cars to export");

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
  doc.setProperties({
    title: `${t.title} – CarHive`,
    subject: t.title,
    creator: "CarHive",
    author: "CarHive",
  });

  // === Header band (red) ==================================================
  doc.setFillColor(...COLOR_RED);
  doc.rect(0, 0, PAGE_W, 22, "F");
  // CarHive wordmark — drawn with stroked hexagon + text (vector, no asset round-trip).
  drawHexLogo(doc, MARGIN, 7, 8, 8);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("CarHive", MARGIN + 11, 13);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(t.subtitle.toUpperCase(), MARGIN + 11, 17.5);

  const dateStr = new Intl.DateTimeFormat(locale === "en" ? "en-CH" : `${locale}-CH`, {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).format(new Date());
  doc.setFontSize(8);
  doc.text(`${t.date}: ${dateStr}`, PAGE_W - MARGIN, 13, { align: "right" });
  doc.text("carhive.ch", PAGE_W - MARGIN, 17.5, { align: "right" });

  // === Title ==============================================================
  doc.setTextColor(...COLOR_TXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(`${t.title} · ${validCars.length}/3`, MARGIN, 33);
  doc.setDrawColor(...COLOR_LINE);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 36, PAGE_W - MARGIN, 36);

  // === Hero row (images + headline per car) ===============================
  const heroTop = 41;
  const heroH = 44;
  const colW = (PAGE_W - 2 * MARGIN) / validCars.length;
  const imgs = await Promise.all(
    validCars.map((c) => imageToDataUrl(absImageUrl((c.images || [])[0], apiBase), 320))
  );
  validCars.forEach((c, i) => {
    const x = MARGIN + i * colW;
    // image area
    const imgX = x + 1.5;
    const imgY = heroTop;
    const imgW = colW - 3;
    const imgH = 26;
    doc.setFillColor(244, 244, 245);
    doc.roundedRect(imgX, imgY, imgW, imgH, 1.5, 1.5, "F");
    if (imgs[i]) {
      try { doc.addImage(imgs[i], "JPEG", imgX, imgY, imgW, imgH); } catch { /* ignore */ }
    }
    // boost pill
    if (c.featured) {
      doc.setFillColor(...COLOR_RED);
      doc.roundedRect(imgX + 1, imgY + 1, 18, 4.5, 1, 1, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.text(t.boost.toUpperCase(), imgX + 10, imgY + 4.2, { align: "center" });
    }
    // title
    doc.setTextColor(...COLOR_TXT_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    const titleText = c.title || `${c.brand || ""} ${c.model || ""}`.trim() || "—";
    const titleLines = doc.splitTextToSize(titleText, colW - 3);
    doc.text(titleLines.slice(0, 2), x + 1.5, imgY + imgH + 5);
    // price
    doc.setTextColor(...COLOR_RED);
    doc.setFontSize(11);
    doc.text(fmtCHF(c.price_chf) || "—", x + 1.5, imgY + imgH + 11);
    // dealer tag
    if (c.seller_dealer?.is_dealer) {
      doc.setTextColor(...COLOR_TXT_MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      const dealerLabel = c.seller_dealer.company_name
        ? `${t.dealer} · ${c.seller_dealer.company_name}`
        : t.dealer;
      const dealerLines = doc.splitTextToSize(dealerLabel, colW - 3);
      doc.text(dealerLines.slice(0, 1), x + 1.5, imgY + imgH + 15);
    }
  });

  // === Compare table ======================================================
  const head = [[t.attribute, ...validCars.map((c) => `${c.brand || ""} ${c.model || ""}`.trim() || "—")]];
  const body = (attributes || []).map((a) => {
    const cells = validCars.map((c) => (a.fmt ? a.fmt(c?.[a.key], c) : (c?.[a.key] ?? "—")));
    return [a.label, ...cells];
  });
  // Pre-compute per-cell metadata for styling (best-of + diff-row).
  const rowDiff = (attributes || []).map((a) => attrDiffers(a.key, validCars));
  const bestIdxPerRow = (attributes || []).map((a) => attrBestIndex(a, validCars));

  autoTable(doc, {
    startY: heroTop + heroH + 16,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 2.2,
      lineColor: COLOR_LINE,
      lineWidth: 0.15,
      textColor: COLOR_TXT_DARK,
      valign: "middle",
    },
    headStyles: {
      fillColor: COLOR_BG_DARK,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
      cellPadding: 3,
    },
    columnStyles: { 0: { fontStyle: "bold", textColor: COLOR_TXT_MUTED, cellWidth: 36 } },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      const rowIdx = data.row.index;
      const colIdx = data.column.index;
      // Highlight winning cell per row.
      if (colIdx > 0 && bestIdxPerRow[rowIdx] === colIdx - 1) {
        data.cell.styles.fillColor = COLOR_HIGHLIGHT;
        data.cell.styles.fontStyle = "bold";
      } else if (colIdx > 0 && rowDiff[rowIdx]) {
        // Soft diff wash on cells that differ (when no clear winner).
        data.cell.styles.fillColor = COLOR_DIFF;
      }
    },
    didDrawPage: (data) => {
      // Footer per page.
      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(...COLOR_TXT_MUTED);
      doc.setFont("helvetica", "normal");
      doc.text(t.footer, MARGIN, PAGE_H - 8);
      doc.text(`${t.page} ${pageNum} / ${pageCount}`, PAGE_W - MARGIN, PAGE_H - 8, { align: "right" });
      // Re-draw header band on subsequent pages (lightweight).
      if (pageNum > 1) {
        doc.setFillColor(...COLOR_RED);
        doc.rect(0, 0, PAGE_W, 10, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("CarHive · " + t.title, MARGIN, 7);
      }
      // Reset table margin for new pages to leave room below the band.
      if (pageNum > 1) data.settings.margin.top = 14;
    },
  });

  doc.save(buildCompareFilename(validCars));
}

/** Tiny vector hex-shape for the header (no external asset). */
function drawHexLogo(doc, x, y, w, h) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  const r = Math.min(w, h) / 2;
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(255, 255, 255);
  // jsPDF needs a polygon → use lines + fill
  const path = pts.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(" ") + " Z";
  // Fallback: jsPDF lacks SVG path; emulate with triangles.
  doc.triangle(pts[0][0], pts[0][1], pts[1][0], pts[1][1], pts[2][0], pts[2][1], "F");
  doc.triangle(pts[0][0], pts[0][1], pts[2][0], pts[2][1], pts[3][0], pts[3][1], "F");
  doc.triangle(pts[0][0], pts[0][1], pts[3][0], pts[3][1], pts[4][0], pts[4][1], "F");
  doc.triangle(pts[0][0], pts[0][1], pts[4][0], pts[4][1], pts[5][0], pts[5][1], "F");
  // suppress unused-var warning
  void path;
}
